import { useEffect, useState, useCallback } from "react";
import { UserPlus, Trash, Key, Copy, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { RoleBadge } from "../components/badges";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const ALL_ROLES = [
  ["sales", "Sales"],
  ["sales_lead", "Sales Lead"],
  ["production", "Production"],
  ["dispatch", "Dispatch"],
  ["admin", "Admin / Accounts"],
  ["customer", "Customer"],
];

const nextRoles = (value, role, checked) => {
  if (!checked) return value.filter((r) => r !== role);
  if (role === "customer") return ["customer"];
  return [...value.filter((r) => r !== "customer"), role];
};

const RoleChecks = ({ value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-2">
    {ALL_ROLES.map(([v, label]) => (
      <label key={v} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
        <input type="checkbox" data-testid={`${prefix}-role-${v}`} checked={value.includes(v)}
          onChange={(e) => onChange(nextRoles(value, v, e.target.checked))} />
        {label}
      </label>
    ))}
  </div>
);

const emptyForm = { name: "", identifier: "", password: "", roles: ["sales"], customer_name: "", customer_location: "", id_type: "aadhaar", id_number: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetRequests, setResetRequests] = useState([]);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [rolesTarget, setRolesTarget] = useState(null);
  const [rolesValue, setRolesValue] = useState([]);

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/auth/reset-requests").then(({ data }) => setResetRequests(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const setF = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.roles.length === 0) {
      toast.error("Select at least one role");
      return;
    }
    setSaving(true);
    try {
      const ident = form.identifier.trim();
      const body = { name: form.name, password: form.password, roles: form.roles };
      if (ident.includes("@")) body.email = ident;
      else body.username = ident;
      if (form.roles.includes("customer")) {
        body.customer_name = form.customer_name;
        body.customer_location = form.customer_location;
        if (form.id_number.trim()) {
          body.id_type = form.id_type;
          body.id_number = form.id_number;
        }
      }
      await api.post("/users", body);
      toast.success(`User ${form.name} created`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name} (${u.email || u.username})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setPassword = async () => {
    if (pwValue.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwSaving(true);
    try {
      const { data } = await api.put(`/users/${pwTarget.id}/password`, { new_password: pwValue });
      toast.success(data.message || `Password updated for ${pwTarget.name}`);
      setPwTarget(null);
      setPwValue("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const saveRoles = async () => {
    try {
      await api.put(`/users/${rolesTarget.id}/roles`, { roles: rolesValue });
      toast.success("Roles updated");
      setRolesTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyResetLink = (token) => {
    const link = `${window.location.origin}/reset-password?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Reset link copied — share it with the user");
  };

  return (
    <div data-testid="users-page" className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">User Management</h1>
        <p className="mt-1 text-sm text-slate-500">Create and manage team accounts and roles.</p>
      </div>

      <form onSubmit={submit} className="rounded-md border border-slate-200 bg-white p-5">
        <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <UserPlus size={18} weight="duotone" /> Add User
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</label>
            <input data-testid="user-name-input" required value={form.name} onChange={(e) => setF("name", e.target.value)}
              placeholder="Full name"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email or Username *</label>
            <input data-testid="user-identifier-input" required value={form.identifier} onChange={(e) => setF("identifier", e.target.value)}
              placeholder="user@dcn.com or username"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password *</label>
            <input data-testid="user-password-input" required type="password" minLength={6} value={form.password}
              onChange={(e) => setF("password", e.target.value)} placeholder="Min 6 characters"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Roles *</label>
            <div className="mt-2">
              <RoleChecks prefix="user" value={form.roles} onChange={(v) => setF("roles", v)} />
            </div>
          </div>
        </div>
        {form.roles.includes("customer") && (
          <div className="mt-3 grid gap-3 rounded-md border border-violet-200 bg-violet-50 p-3 sm:grid-cols-2" data-testid="customer-link-fields">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer / Business Name *</label>
              <input data-testid="user-customer-name-input" required value={form.customer_name}
                onChange={(e) => setF("customer_name", e.target.value)} placeholder="e.g. Sharma Traders"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</label>
              <input data-testid="user-customer-location-input" value={form.customer_location}
                onChange={(e) => setF("customer_location", e.target.value)} placeholder="City / area"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">ID Type</label>
              <select data-testid="user-customer-id-type" value={form.id_type} onChange={(e) => setF("id_type", e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm outline-none">
                <option value="aadhaar">Aadhaar</option>
                <option value="gst">GST</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Aadhaar / GST Number</label>
              <input data-testid="user-customer-id-number" value={form.id_number}
                onChange={(e) => setF("id_number", e.target.value)} placeholder="Required if customer is new to Masters"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            </div>
          </div>
        )}
        <button data-testid="create-user-button" type="submit" disabled={saving}
          className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
          {saving ? "Creating…" : "Create user"}
        </button>
      </form>

      {resetRequests.length > 0 && (
        <div data-testid="reset-requests-section" className="rounded-md border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-base font-bold text-slate-900 md:text-lg">Pending Password Reset Requests</h2>
          <p className="mt-0.5 text-xs text-slate-500">Copy the reset link and share it with the user (valid for 1 hour).</p>
          <div className="mt-3 space-y-2">
            {resetRequests.map((r) => (
              <div key={r.token} data-testid={`reset-request-${r.email}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2 text-sm">
                <span className="font-medium">{r.email}</span>
                <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                <button data-testid={`copy-reset-link-${r.email}`} onClick={() => copyResetLink(r.token)}
                  className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
                  <Copy size={13} weight="duotone" /> Copy reset link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pwTarget && (
        <Dialog open onOpenChange={(o) => { if (!o) { setPwTarget(null); setPwValue(""); } }}>
          <DialogContent className="max-w-md" data-testid="set-password-panel">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key size={18} weight="duotone" /> Set new password
              </DialogTitle>
              <DialogDescription>
                For <span className="font-semibold text-slate-900">{pwTarget.name}</span> ({pwTarget.email || pwTarget.username}).
                They will be asked to change it after their next login.
              </DialogDescription>
            </DialogHeader>
            <input data-testid="set-password-input" type="password" minLength={6} value={pwValue} autoFocus
              onChange={(e) => setPwValue(e.target.value)} placeholder="New password (min 6 characters)"
              onKeyDown={(e) => e.key === "Enter" && setPassword()}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            <div className="flex justify-end gap-2">
              <button data-testid="cancel-password-button" onClick={() => { setPwTarget(null); setPwValue(""); }}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
                Cancel
              </button>
              <button data-testid="save-password-button" onClick={setPassword} disabled={pwSaving}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
                {pwSaving ? "Saving…" : "Save password"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {rolesTarget && (
        <div data-testid="edit-roles-panel" className="flex flex-wrap items-center gap-4 rounded-md border border-slate-300 bg-slate-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Roles for {rolesTarget.email || rolesTarget.username}
          </span>
          <RoleChecks prefix="edit" value={rolesValue} onChange={setRolesValue} />
          <button data-testid="save-roles-button" onClick={saveRoles} disabled={rolesValue.length === 0}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-50">
            Save roles
          </button>
          <button data-testid="cancel-roles-button" onClick={() => setRolesTarget(null)}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            Cancel
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="p-3">Name</th>
              <th className="p-3">Email / Username</th>
              <th className="p-3">Roles</th>
              <th className="p-3">Created</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email || u.username}`} className="border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50">
                <td className="p-3 font-medium">{u.name}{u.id === user?.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}</td>
                <td className="p-3 text-slate-500">{u.email || u.username}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    {(u.roles || []).map((r) => <RoleBadge key={r} role={r} />)}
                  </div>
                </td>
                <td className="p-3 text-slate-500">{u.created_at ? String(u.created_at).slice(0, 10) : "—"}</td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button data-testid={`edit-roles-button-${u.email || u.username}`} title="Edit roles"
                      onClick={() => { setRolesTarget(rolesTarget?.id === u.id ? null : u); setRolesValue(u.roles || []); }}
                      className="text-slate-400 transition-colors duration-150 hover:text-slate-900">
                      <ShieldCheck size={16} weight="duotone" />
                    </button>
                    <button data-testid={`set-password-button-${u.email || u.username}`} title="Set new password"
                      onClick={() => { setPwTarget(pwTarget?.id === u.id ? null : u); setPwValue(""); }}
                      className="text-slate-400 transition-colors duration-150 hover:text-slate-900">
                      <Key size={16} weight="duotone" />
                    </button>
                    {u.id !== user?.id && (
                      <button data-testid={`delete-user-button-${u.email || u.username}`} onClick={() => remove(u)}
                        className="text-slate-400 transition-colors duration-150 hover:text-red-600">
                        <Trash size={16} weight="duotone" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
